import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { resolveVaultApiUrl } from './env-profiles';
import {
  applyVaultXTenantHeader,
  shouldSkipVaultTenantHeaderForUrl,
} from './vault-tenant-header.util';
import { VaultAccountBlockedService } from './vault-account-blocked.service';
import { isVaultAuthMeUrl, isVaultLoginRoute, resolveVaultAccountBlockedState } from './vault-account-blocked.util';
import { vaultUserIsSuperadminFromSession } from './vault-user-roles.util';

/** multipart/form-data debe dejar el boundary al navegador; no forzar application/json. */
function vaultJsonHeadersIfNeeded(body: unknown): Record<string, string> {
  if (body instanceof FormData) {
    return {};
  }
  return { 'Content-Type': 'application/json' };
}

function cloneVaultRequest<T>(
  req: HttpRequest<T>,
  fullUrl: string,
  accessToken: string | null,
): HttpRequest<T> {
  const skipTenantScope = shouldSkipVaultTenantHeaderForUrl(req.url);
  const headers: Record<string, string> = { ...vaultJsonHeadersIfNeeded(req.body) };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  if (!skipTenantScope) {
    applyVaultXTenantHeader(headers, req.url);
  }
  let params = req.params;
  if (shouldStripTenantIdQueryParam(req.url, req.method) && params.has('tenant_id')) {
    params = params.delete('tenant_id');
  }
  return req.clone({ url: fullUrl, setHeaders: headers, params });
}

/** Solo listado GET global superadmin; no quitar `tenant_id` en POST create. */
function shouldStripTenantIdQueryParam(url: string, method: string): boolean {
  if (method.toUpperCase() !== 'GET') return false;
  const path = (url || '').split('?')[0].replace(/\/+$/, '');
  return path.endsWith('/api/admin/users') && vaultUserIsSuperadminFromSession();
}

export function createApiVaultInterceptor(_apiVaultBase?: string): HttpInterceptorFn {
  return (req, next) => {
    const accountBlocked = inject(VaultAccountBlockedService);

    if (req.url.includes('/refresh')) {
      return next(req);
    }

    if (req.url.startsWith('http')) {
      return next(req);
    }

    const vaultBase = resolveVaultApiUrl() || _apiVaultBase || '';
    const fullUrl = `${vaultBase}${req.url}`;
    const ACCESS_TOKEN = localStorage.getItem('access_token');

    const authReq = cloneVaultRequest(req, fullUrl, ACCESS_TOKEN);

    return next(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        const blockedState = resolveVaultAccountBlockedState(error);
        if (blockedState && isVaultAuthMeUrl(req.url) && !isVaultLoginRoute()) {
          accountBlocked.applyFromError(error);
          return throwError(() => error);
        }

        /** El 401 lo recupera el errorInterceptor (refresh único vía AuthService + retry). */
        return throwError(() => error);
      }),
    );
  };
}
